/**
 * WeeklyBarChart — Stacked weekly bar chart with per-project hover tooltips.
 *
 * Designed to be reused across analytics surfaces. Each bar can be stacked
 * by project (breakdown). Hover shows a tooltip at the pointer with project
 * name + value (optionally with a formatted subvalue like $ amount).
 *
 * Props:
 *   data       — [{ date, label, value, breakdown: [{ jobId, jobTitle, value, color? }] }]
 *   height     — chart height in px (default 120)
 *   formatValue — (value) => string for bar-top label (e.g. v => `$${v}`)
 *   formatBreakdownValue — (bd) => string for tooltip row (e.g. bd => `${bd.value}`)
 *   projectColors — Map<jobId, color> fallback when breakdown items lack color
 *   color      — single color when breakdown has 1 entry or no color map (default teal)
 *   emptyMessage — string shown when data is all-zero (optional)
 */

import { useState } from 'react';

const DEFAULT_COLOR = '#5DCAA5';
const FALLBACK_PALETTE = ['#5DCAA5', '#FAC775', '#AFA9EC', '#F8A1B5', '#6EC1E4', '#C6D886'];

export default function WeeklyBarChart({
  data = [],
  height = 120,
  formatValue,
  formatBreakdownValue,
  projectColors,
  color = DEFAULT_COLOR,
  emptyMessage,
}) {
  const [tooltip, setTooltip] = useState(null);
  const [containerRef, setContainerRef] = useState(null);

  if (!data || data.length === 0) {
    return <div style={{ padding: 20, fontSize: 11, color: 'var(--text-muted)', textAlign: 'center' }}>{emptyMessage || 'No data'}</div>;
  }

  const max = Math.max(1, ...data.map(d => d.value || 0));
  const allZero = data.every(d => (d.value || 0) === 0);

  const W = 800;
  // Reserve top space so HTML value labels (rendered as absolute divs ~14px tall) don't overlap the tallest bar.
  const padding = { top: 18, bottom: 4, left: 0, right: 0 };
  const chartAreaHeight = height - padding.top - padding.bottom;
  const barGap = 6;
  const barWidth = Math.max(4, (W - barGap * (data.length - 1)) / data.length);

  function colorFor(jobId, index) {
    if (projectColors && projectColors.get && projectColors.get(jobId)) return projectColors.get(jobId);
    return FALLBACK_PALETTE[index % FALLBACK_PALETTE.length];
  }

  function breakdownFor(d) {
    const list = (d.breakdown && d.breakdown.length > 0) ? d.breakdown : [{ jobId: 'total', jobTitle: 'Total', value: d.value || 0 }];
    return list.map((b, i) => ({ ...b, color: b.color || colorFor(b.jobId, i) || color }));
  }

  return (
    <div ref={setContainerRef} style={{ position: 'relative', width: '100%' }}>
      <svg width="100%" height={height} viewBox={`0 0 ${W} ${height}`} preserveAspectRatio="none" style={{ display: 'block', overflow: 'visible' }}>
        {data.map((d, i) => {
          const x = i * (barWidth + barGap);
          const value = d.value || 0;
          const breakdown = breakdownFor(d);
          const totalH = Math.max(2, (value / max) * chartAreaHeight);

          // Stacked segments, bottom-up (top of stack visually = end of array in SVG)
          let yOffset = 0;
          const segments = [];
          for (let bi = 0; bi < breakdown.length; bi++) {
            const b = breakdown[bi];
            const segH = value > 0 ? (b.value / value) * totalH : 0;
            if (segH <= 0) continue;
            const segY = padding.top + chartAreaHeight - yOffset - segH;
            segments.push(
              <rect key={`${i}-${b.jobId}-${bi}`} x={x} y={segY} width={barWidth} height={segH} fill={b.color} rx={2} opacity={0.9} />,
            );
            yOffset += segH;
          }

          return (
            <g key={i}
              onMouseEnter={(e) => {
                const cr = containerRef?.getBoundingClientRect();
                setTooltip({
                  mouseX: e.clientX - (cr?.left || 0),
                  mouseY: e.clientY - (cr?.top || 0),
                  label: d.label,
                  value,
                  breakdown,
                });
              }}
              onMouseMove={(e) => {
                const cr = containerRef?.getBoundingClientRect();
                setTooltip(prev => prev ? { ...prev, mouseX: e.clientX - (cr?.left || 0), mouseY: e.clientY - (cr?.top || 0) } : prev);
              }}
              onMouseLeave={() => setTooltip(null)}
              style={{ cursor: value > 0 ? 'pointer' : 'default' }}
            >
              {/* Hit area for empty weeks so tooltip still works */}
              <rect x={x} y={padding.top} width={barWidth} height={chartAreaHeight} fill="transparent" />
              {segments}
              {/* Empty-week placeholder line */}
              {value === 0 && (
                <rect x={x} y={padding.top + chartAreaHeight - 2} width={barWidth} height={2} fill="#e5e7eb" rx={1} />
              )}
            </g>
          );
        })}
      </svg>

      {/* HTML value labels above each bar (avoid SVG text stretching under preserveAspectRatio="none") */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height,
        display: 'flex', pointerEvents: 'none',
      }}>
        {data.map((d, i) => {
          const value = d.value || 0;
          if (value === 0) return <div key={i} style={{ flex: 1 }} />;
          const totalH = Math.max(2, (value / max) * chartAreaHeight);
          const barTopPx = padding.top + chartAreaHeight - totalH;
          return (
            <div key={i} style={{ flex: 1, position: 'relative' }}>
              <div style={{
                position: 'absolute',
                left: 0, right: 0,
                top: Math.max(0, barTopPx - 14),
                textAlign: 'center',
                fontSize: 10,
                fontFamily: 'var(--font-mono)',
                color: 'var(--text-muted)',
                whiteSpace: 'nowrap',
              }}>
                {formatValue ? formatValue(value) : value.toLocaleString()}
              </div>
            </div>
          );
        })}
      </div>

      {/* HTML date labels below chart (avoid SVG text stretching) */}
      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0 0' }}>
        {data.map((d, i) => (
          <span key={i} style={{ flex: 1, textAlign: 'center', fontSize: 9, fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>
            {d.label}
          </span>
        ))}
      </div>

      {allZero && (
        <div style={{ position: 'absolute', top: '50%', left: 0, right: 0, textAlign: 'center', fontSize: 10, color: 'var(--text-muted)', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
          No activity in this window
        </div>
      )}

      {/* Tooltip */}
      {tooltip && (
        <div style={{
          position: 'absolute',
          top: tooltip.mouseY - 8,
          left: tooltip.mouseX + 12,
          transform: 'translateY(-100%)',
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: 6,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          padding: '6px 10px',
          zIndex: 10,
          minWidth: 140,
          pointerEvents: 'none',
        }}>
          <div style={{ fontSize: 9, fontFamily: 'var(--font-mono)', color: '#9ca3af', marginBottom: 3 }}>{tooltip.label}</div>
          {tooltip.breakdown.length > 0 && tooltip.value > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {tooltip.breakdown.map((b, bi) => (
                <div key={bi} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10 }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: b.color, flexShrink: 0 }} />
                  <span style={{ color: '#0a0a0a', fontWeight: 500, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 180 }}>
                    {b.jobTitle}
                  </span>
                  <span style={{ fontFamily: 'var(--font-mono)', color: '#0a0a0a', fontWeight: 600 }}>
                    {formatBreakdownValue ? formatBreakdownValue(b) : b.value.toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ fontSize: 11, color: '#9ca3af' }}>No activity</div>
          )}
        </div>
      )}
    </div>
  );
}
